import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Grid, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { Add, Edit, Groups, ArrowForwardIos } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { updateTableActions } from '../../store/update-table-slice';
import '../../App.css';
import EmployeeSalaryForm from './EmployeeSalaryForm';
import EmpSalComponentsTable from '../table-data/EmpSalComponentsTable'; // The component you need to update
import Popup from '../Popup';
import ViewEmployeeLeaves from './ViewEmployeeLeaves';
import Notification from '../Notification';

function ViewEmpSalComponents(props) {
  const [openPopup1, setOpenPopup1] = useState(false);
  const [openPopup2, setOpenPopup2] = useState(false);
  const [openEditPopup, setOpenEditPopup] = useState(false);
  const [editInitialValues, setEditInitialValues] = useState(null);
  const [basicSalary, setBasicSalary] = useState('N/A');
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const [tableRefreshKey, setTableRefreshKey] = useState(0);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const updateEmpSalaryComponents = useSelector(state => state.updateTable.updateEmpSalComponents);
  const employeeId = props.fetch_id === 9999 ? props.employeeID : props.fetch_id;

  const fetchBasicSalary = async () => {
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/employee-master/employee-basic/${props.fetch_id}`;
      const response = await axios.get(url);
      if (Array.isArray(response.data) && response.data.length > 0) {
        setBasicSalary(response.data[0].basic);
      } else if (response.data && response.data.basic) {
        setBasicSalary(response.data.basic);
      } else {
        setBasicSalary('N/A');
        console.warn('Unexpected API response:', response.data);
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setNotify({
          isOpen: true,
          message: 'Session Expired!',
          type: 'error'
        });
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else if (error.response && error.response.status === 404) {
        setBasicSalary('Not Found');
      } else {
        setBasicSalary('Error');
        console.error('Error fetching basic salary:', error);
      }
    }
  };

  const handleDeleteSuccess = () => {
    fetchBasicSalary();
    setTableRefreshKey(prev => prev + 1);
  };

  useEffect(() => {
    if (props.fetch_id) {
      fetchBasicSalary();
    }
  }, [props.fetch_id]);

  useEffect(() => {
    if (updateEmpSalaryComponents) {
      // Re-fetch the basic salary and stop the Redux update flag.
      fetchBasicSalary(); 
      dispatch(updateTableActions.stopUpdateEmpSalComponents());
    }
  }, [updateEmpSalaryComponents, dispatch]);

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  const handleCloseModal = () => {
    setOpenPopup1(false);
  };

  const handleCloseEditModal = () => {
    setOpenEditPopup(false);
    setEditInitialValues(null);
  };

  const getTodayDateString = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // Opens ADD EMPLOYEE SALARY modal pre-filled from latest CTC; creates a NEW section on submit
  const handleOpenEditSalary = async () => {
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/employee-master/view-sal-components/${employeeId}`;
      const response = await axios.get(url);
      const structures = Array.isArray(response.data) ? response.data : [];

      if (structures.length === 0) {
        setNotify({
          isOpen: true,
          message: 'No salary structure found to edit. Please add a CTC structure first.',
          type: 'error'
        });
        return;
      }

      // Latest structure is first (API returns descending by effective date)
      const latest = structures[0];
      const firstComp = latest.components && latest.components.length > 0 ? latest.components[0] : {};

      setEditInitialValues({
        department: latest.department || firstComp.department || '',
        designation: latest.designation || firstComp.designation || '',
        ctc: latest.ctc != null ? String(latest.ctc) : '',
        basic: latest.basic != null ? String(latest.basic) : '',
        isEmployeeActive: firstComp.IsActive === 1 || firstComp.IsActive === true,
        // New section: default effective date to today (editable)
        effectiveDate: getTodayDateString()
      });
      setOpenEditPopup(true);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setNotify({
          isOpen: true,
          message: 'Session Expired!',
          type: 'error'
        });
        setTimeout(() => {
          navigate('/');
        }, 1000);
      } else {
        setNotify({
          isOpen: true,
          message: error.response?.data?.message || error.response?.data?.err_message || 'Failed to load salary structure',
          type: 'error'
        });
      }
    }
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", margin: "10px", padding: "30px", marginTop: "-40px", fontWeight: "500px" }}>
      <Paper elevation={0}>
        <Grid className="form-title" style={{ marginBottom: "-60px" }} container spacing={2}>
          <Grid item>
            <Typography variant="h5" sx={{ textAlign: "center", margin: 5 }}>
              Employee Salary Components
            </Typography>
          </Grid>
          <Grid item>
            <Groups style={{ marginLeft: "-30px" }} sx={{ width: 45, height: 45 }} />
          </Grid>
        </Grid>
        <form autoComplete="off" onSubmit={handleSubmit}>
          <Stack spacing={2} sx={{ margin: "30px" }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              spacing={2}
              sx={{ width: '100%', flexWrap: 'wrap', gap: 2 }}
            >
              <Button
                variant="outlined"
                color="primary"
                onClick={handleOpenEditSalary}
                startIcon={<Edit sx={{ width: "22px", height: "22px" }} />}
                sx={{
                  width: 'auto',
                  minWidth: 'fit-content',
                  height: 40,
                  px: 2,
                  whiteSpace: 'nowrap',
                  textTransform: 'none',
                  flexShrink: 0
                }}
              >
                Copy CTC Structure
              </Button>
              <Button
                variant="outlined"
                color="success"
                onClick={() => setOpenPopup1(true)}
                startIcon={<Add sx={{ width: "24px", height: "24px" }} />}
                sx={{
                  width: 'auto',
                  minWidth: 'fit-content',
                  height: 40,
                  px: 2,
                  whiteSpace: 'nowrap',
                  textTransform: 'none',
                  flexShrink: 0
                }}
              >
                Add New CTC Structure
              </Button>
            </Stack>

            <EmpSalComponentsTable 
              key={`table-${tableRefreshKey}`}
              employeeID={props.employeeID} 
              fetch_id={props.fetch_id} 
              onDeleteSuccess={handleDeleteSuccess}
            />

            <Stack className="end-align">
              <Button
                className="form-btn"
                variant="outlined"
                onClick={() => setOpenPopup2(true)}
                endIcon={<ArrowForwardIos />}
              >
                Next
              </Button>
            </Stack>
          </Stack>
        </form>

        <Popup
          title="Add"
          openPopup={openPopup1}
          setOpenPopup={setOpenPopup1}
        >
          <EmployeeSalaryForm
            employeeID={props.employeeID}
            salCompFetchID={9999}
            fetch_id={props.fetch_id}
            onCloseModal={handleCloseModal}
            onSuccess={() => setTableRefreshKey(prev => prev + 1)}
          />
        </Popup>

        <Popup
          title="Add"
          openPopup={openEditPopup}
          setOpenPopup={(open) => {
            setOpenEditPopup(open);
            if (!open) {
              setEditInitialValues(null);
            }
          }}
        >
          {editInitialValues && (
            <EmployeeSalaryForm
              employeeID={props.employeeID}
              salCompFetchID={9999}
              fetch_id={props.fetch_id}
              initialValues={editInitialValues}
              onCloseModal={handleCloseEditModal}
            />
          )}
        </Popup>

        <Popup
          title="Add"
          openPopup={openPopup2}
          setOpenPopup={setOpenPopup2}
        >
          <ViewEmployeeLeaves
            employeeID={props.employeeID}
            fetch_id={props.fetch_id}
          />
        </Popup>
        <Notification notify={notify} setNotify={setNotify} />
      </Paper>
    </Box>
  );
}

export default ViewEmpSalComponents;
